import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "../../../moment";
import config from "../../../config";
import { useAxiosRequest } from "../../../utils/hooks/useAxios";
import { getBranchesData } from "../../Organization/OrganizationChart/helpers";
import { reloadTable } from "../../../components/SmartTable/reducer";
import TableService from "../../../components/SmartTable/TableService";
import { TABLE_QUALIFICATIONS } from "./methods";
import { deriveQualificationFilters, prepopulateFilter, saveFilterOnLocalStorage } from "./qualificationsHelpers";

/**
 * Encapsulates all state, data fetches, filter derivations, and event handlers
 * for the Qualifications page, keeping the component focused on rendering.
 *
 * @param {object} params
 * @param {object} params.localStorageTableFilters - Persisted filter state from localStorage.
 * @param {string|undefined} params.currentSearchValue - Persisted search string.
 * @param {number|undefined} params.currentPage - Persisted current page number.
 * @returns {object} All state, options, derived filters, handlers, and tableService.
 */
const useQualificationsState = ({ localStorageTableFilters, currentSearchValue, currentPage }) => {
  const dispatch = useDispatch();
  const [totalItems, setTotalItems] = useState();
  const [shouldReset, setShouldReset] = useState();
  const [apiEmployeeStatusOptions, setApiEmployeeStatusOptions] = useState([]);
  const [apiJobTitleOptions, setApiJobTitleOptions] = useState([]);
  const [apiDepartmentsOptions, setApiDepartmentsOptions] = useState([]);
  const [dateRange, setDateRange] = useState(undefined);
  const [apiAreasOptions, setApiAreasOptions] = useState([]);
  const [branchesData, setBranchesData] = useState([]);

  const employeeStatusState = useAxiosRequest({
    route: "employee-status",
    params: {},
    onSuccess: (res) => setApiEmployeeStatusOptions(res?.data?.data)
  });

  const jobTitleState = useAxiosRequest({
    route: "job-titles?field%5B%5D=active&op%5B%5D=eq&term%5B%5D=1&order_by_column%5B%5D=name&order_by_type%5B%5D=asc",
    params: {},
    onSuccess: (res) => setApiJobTitleOptions(res?.data?.data)
  });

  const departmentsState = useAxiosRequest({
    route: "departments?order_by_column%5B%5D=name&order_by_type%5B%5D=asc&with%5B%5D=branch",
    params: {},
    onSuccess: (res) => setApiDepartmentsOptions(res?.data?.data)
  });

  const areasState = useAxiosRequest({
    route: "trg/areas?order_by_column%5B%5D=name&order_by_type%5B%5D=asc",
    params: {},
    onSuccess: (res) => setApiAreasOptions(res?.data?.data)
  });

  const branchesOptions = useMemo(
    () => branchesData.map((branch) => ({ value: branch.id, text: branch.name })),
    [branchesData]
  );

  const {
    contractFilter,
    statusFilter,
    employeeStatusFilter,
    branchFilter,
    departmentFilter,
    areaFilter,
    jobTitleFilter,
    dateFilter
  } = deriveQualificationFilters(localStorageTableFilters);

  const [tableService] = useState(
    new TableService({
      defaultWith: [
        "qualifications.training.validity",
        "qualifications.training.uncompletedCourses.participants",
        "userDetail",
        "userDetail.employeeStatuses",
        "userDetail.branch",
        "activeStaffJobFunctions.staffFunctions.trainingPrograms.activeJobTitleTrainings.training.uncompletedCourses.participants"
      ],
      defaultOrderBy: [
        { column: "last_name", type: "asc" },
        { column: "first_name", type: "asc" }
      ],
      searchColumns: ["name"],
      ...localStorageTableFilters,
      searchValue: currentSearchValue,
      page: currentPage
    })
  );

  useEffect(() => {
    getBranchesData(setBranchesData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore the date-range picker value from localStorage on mount
  useEffect(() => {
    if (dateFilter?.value?.start_value && dateFilter?.value?.end_value) {
      setDateRange([moment(dateFilter.value.start_value), moment(dateFilter.value.end_value)]);
    }
  }, [dateFilter?.value?.start_value, dateFilter?.value?.end_value]);

  /**
   * Custom table-change handler that updates sorting, column filters, and pagination.
   *
   * @param {{ pagination, filters, sorter, tableService, getTableData, columns }} params
   */
  const customHandleTableChange = ({ pagination, filters, sorter, tableService: ts, getTableData, columns }) => {
    ts.setPage(pagination.current);
    ts.removeAllOrderBy().manageOrderBy({ column: sorter.field, type: sorter.order });
    ts.manageColumnFilters(filters, columns);
    saveFilterOnLocalStorage([...ts.anotherFilters]);
    getTableData(ts.getFilters());
    localStorage.setItem("smartTableLastPage", pagination.current);
  };

  /**
   * Resets all external filters, clears SmartTable column filters, and purges
   * all qualification-related localStorage keys.
   */
  const handleClearFilters = () => {
    dispatch(reloadTable({ name: TABLE_QUALIFICATIONS, resetFilters: true }));
    setShouldReset(Date.now());
    setDateRange(undefined);
    localStorage.removeItem("qualificationsPageStateApply");
    localStorage.removeItem("qualificationsPageState");
    localStorage.removeItem("smartTableLastPage");
    localStorage.removeItem("smartTableLastSearch");
  };

  /**
   * Updates a single named filter in the TableService and triggers a table reload.
   * Passing `undefined` as `value` removes the filter entirely.
   *
   * @param {string} filterName - The `nameKey` identifying the filter.
   * @param {*} value - The new filter value, or `undefined` to clear.
   */
  const handleFilterChange = (filterName, value) => {
    tableService.removeAnotherFilter(filterName);
    if (value !== undefined) {
      tableService.addAnotherFilter({ nameKey: filterName, value });
    }
    saveFilterOnLocalStorage([...tableService.anotherFilters]);
    tableService.setPage(1);
    dispatch(reloadTable({ name: TABLE_QUALIFICATIONS }));
  };

  /**
   * Handles date range selection. Converts moment values to the API's expected
   * format and updates the filter.
   *
   * @param {Array<moment>|null} value - Selected [start, end] moment pair, or null when cleared.
   */
  const handleDateChange = (value) => {
    if (value) {
      const newDateFilter = {
        start_value: value[0].format(config.defaultDateFormat),
        end_value: value[1].format(config.defaultDateFormat)
      };
      handleFilterChange("dateFilter", newDateFilter);
      setDateRange(value);
    } else {
      handleFilterChange("dateFilter", undefined);
      setDateRange(undefined);
    }
  };

  return {
    totalItems,
    setTotalItems,
    shouldReset,
    dateRange,
    branchesOptions,
    contractFilter,
    statusFilter,
    employeeStatusFilter,
    branchFilter,
    departmentFilter,
    areaFilter,
    jobTitleFilter,
    dateFilter,
    tableService,
    employeeStatusState,
    jobTitleState,
    departmentsState,
    areasState,
    apiDepartmentsOptions,
    apiAreasOptions,
    apiJobTitleOptions,
    apiEmployeeStatusOptions,
    customHandleTableChange,
    handleClearFilters,
    handleFilterChange,
    prepopulateFilter,
    handleDateChange
  };
};

export default useQualificationsState;
